import { Fade } from '@mui/material'
import React, { CSSProperties, ReactElement, useCallback } from 'react'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import { classNames } from '../../utils/cssUtils'
import './Modal.scss'
import { MVPButton } from '../Buttons/MVPButton'

export interface AModalControlProps {
	handler: readonly [boolean, (value: boolean) => void]
	modalStyle?: CSSProperties
}

export interface ModalProps extends AModalControlProps {
	children: React.ReactNode
	id?: string
	dropdown?: boolean
}

/**
 * Wraps `children` in a modal that displays fullscreen
 * @param handler [isDisplaying, setDisplaying] (ie. useState<boolean>)
 */
export const Modal = ({ children, id, handler, modalStyle, dropdown }: ModalProps) => {
	const [isShowing, setShowing] = handler

	const onClickBackdrop = useCallback(
		(e: React.MouseEvent<HTMLElement>) => {
			setShowing(false)
			e.stopPropagation()
		},
		[handler],
	)

	const onClickModal = useCallback((e: React.MouseEvent<HTMLElement>) => {
		e.stopPropagation()
	}, [])

	const modalCs = classNames('modal mvp-box', { 'is-dropdown': !!dropdown })

	return (
		<Fade in={isShowing} timeout={300} unmountOnExit>
			<div className='modal-backdrop' onClick={onClickBackdrop}>
				<div className={modalCs} id={id} onClick={onClickModal} style={modalStyle}>
					{children}
				</div>
			</div>
		</Fade>
	)
}

export const createUnstyledModal = NiceModal.create

export const createMVPModal = <Props extends {}>(id: string, Component: React.FC<Props>, disableBackgroundClick = false) => {
	return NiceModal.create<Props>((props: Props) => {
		const modal = useModal()
		const clickBackground = () => {
			if (!disableBackgroundClick){
				modal.hide()
			}
		}

		return (
			<Fade in={modal.visible} timeout={300} unmountOnExit>
				<div className='modal-backdrop' onClick={clickBackground}>
					<div
						id={id}
						className='modal mvp-box'
						onClick={(e) => {
							e.stopPropagation()
						}}
					>
						<Component {...props} />
					</div>
				</div>
			</Fade>
		)
	})
}
